import { useEffect, useMemo, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Container, Stack, Text, Loader, Alert, Group, SegmentedControl } from '@mantine/core'
import { IconAlertCircle } from '@tabler/icons-react'
import { AdminNavbar } from '@/components/AdminNavbar/AdminNavbar'
import { useGuests } from '@/hooks/useGuests'
import { isAdminAuthenticated, refreshAdminSession } from '@/lib/auth'
import styles from './invitation-cards.module.css'

export const Route = createFileRoute('/admin/invitation-cards')({
  component: AdminInvitationCards,
})

function AdminInvitationCards() {
  const navigate = useNavigate()
  const { guests, loading, error } = useGuests()
  const [filter, setFilter] = useState<'all' | 'not_sent'>('all')

  useEffect(() => {
    if (!isAdminAuthenticated()) {
      navigate({ to: '/admin/login' })
      return
    }

    refreshAdminSession()

    const interval = setInterval(() => {
      if (!isAdminAuthenticated()) {
        navigate({ to: '/admin/login' })
      }
    }, 60000)

    return () => clearInterval(interval)
  }, [navigate])

  const eligibleGuests = useMemo(() => {
    const withPin = guests.filter((guest) => guest.pin && guest.primary_name)
    if (filter === 'not_sent') {
      return withPin.filter((guest) => !guest.invite_sent)
    }
    return withPin
  }, [guests, filter])

  return (
    <div className={styles.adminLayout}>
      <AdminNavbar />
      <Container size="xl" className={styles.cardsContainer}>
        <Stack gap="lg" className={styles.controls}>
          <Text fw={700} fz="xl">
            Einladungskarten
          </Text>
          <Text c="dimmed">
            Auf dieser Seite wird für jede eingeladene Person eine druckfertige Einladungskarte
            erzeugt. Verwenden Sie die Druckfunktion Ihres Browsers, um die Karten auf A5-Papier
            auszugeben.
          </Text>

          <Group justify="flex-start">
            <SegmentedControl
              value={filter}
              onChange={(value) => setFilter(value as 'all' | 'not_sent')}
              data={[
                { label: 'Alle Gäste', value: 'all' },
                { label: 'Noch nicht eingeladen', value: 'not_sent' },
              ]}
            />
          </Group>

          {loading && (
            <Stack align="center" gap="sm">
              <Loader />
              <Text>Lade Gästedaten…</Text>
            </Stack>
          )}

          {error && (
            <Alert color="red" icon={<IconAlertCircle size={16} />}>
              Beim Laden der Gäste ist ein Fehler aufgetreten: {error}
            </Alert>
          )}

          {!loading && !error && eligibleGuests.length === 0 && (
            <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
              Es wurden keine Gäste mit gültiger PIN gefunden. Bitte legen Sie zunächst Gäste mit
              PIN an, bevor Sie Einladungskarten drucken.
            </Alert>
          )}
        </Stack>
      </Container>
    </div>
  )
}
